/**
 * Support ticket API — SCK-549 PR-8.
 *
 * Thin fetch wrappers around the ticket-list + ticket-detail endpoints
 * coord PR-3 stood up. Follows the direct-fetch style used by
 * pages/Access/api.js.
 *
 * API_URL already carries the "/api" suffix per
 * memory/feedback_console_api_url.md. Never write `${API_URL}/api/...`.
 */

import { API_URL } from "../../helpers/constants";
import { getToken } from "../../helpers/tokenStorage";

const authHeaders = () => {
  const t = getToken();
  return t
    ? { Authorization: `Bearer ${t}`, "Content-Type": "application/json" }
    : { "Content-Type": "application/json" };
};

async function jsonOrThrow(res) {
  if (res.ok) return res.text().then((t) => (t ? JSON.parse(t) : null));
  const body = await res.text().catch(() => "");
  // Do NOT echo server body verbatim to the user — coord may
  // reflect the request in some 4xx bodies for validation. Truncate
  // aggressively so nothing PII-shaped leaks into a toast.
  const err = new Error(`HTTP ${res.status}`);
  err.status = res.status;
  err.body = body.slice(0, 200);
  throw err;
}

/**
 * GET /v1/support/tickets — list caller's tickets.
 * Returns an array; unwraps the coord `{ items: [] }` envelope if
 * present so consumers can iterate directly.
 */
export function listMyTickets() {
  return fetch(`${API_URL}/v1/support/tickets`, { headers: authHeaders() })
    .then(jsonOrThrow)
    .then((data) => {
      if (!data) return [];
      if (Array.isArray(data)) return data;
      if (Array.isArray(data.items)) return data.items;
      if (Array.isArray(data.tickets)) return data.tickets;
      return [];
    });
}

/**
 * GET /v1/support/tickets/{key} — ticket detail.
 * 404 propagates as an Error with status=404 so the page can render
 * an EmptyState instead of a scary error.
 */
export function getTicket(key) {
  if (!key) return Promise.reject(new Error("ticket key required"));
  return fetch(
    `${API_URL}/v1/support/tickets/${encodeURIComponent(key)}`,
    { headers: authHeaders() }
  ).then(jsonOrThrow);
}
